import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Shield,
  Sparkles,
  Search,
  AlertTriangle,
  Info,
  Syringe,
  Clock,
  Brain,
  Droplet,
  Pill,
  Heart,
  Wind,
  Stethoscope,
  Zap,
  ThermometerSun
} from 'lucide-react';
import { toast } from 'sonner';

export default function InfectionsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Protocoles d'infections par catégorie
  const infectionProtocols = {
    urinaires: {
      name: "Infections Urinaires",
      icon: <Droplet className="w-5 h-5" />,
      color: "bg-blue-500/10 text-blue-400 border-blue-500/30",
      protocols: [
        {
          name: "Colonisation Urinaire",
          indication: "Bactériurie asymptomatique",
          traitement: "Pas d'antibiothérapie en règle générale",
          exception: "Grossesse : Amoxicilline 1g x3/j OU Fosfomycine 3g dose unique",
          duree: "7 jours (grossesse)",
          alert: "Traitement systématique uniquement chez la femme enceinte"
        },
        {
          name: "Cystite Aiguë Simple",
          indication: "Femme jeune, sans complication",
          traitement: "Fosfomycine 3g dose unique OU Pivmécillinam 400mg x2/j",
          alternatives: "Nitrofurantoïne 100mg x3/j",
          duree: "1 jour (fosfomycine) ou 3 jours",
          notes: "Préférer traitement court. ECBU facultatif"
        },
        {
          name: "Cystite à Risque de Complication",
          indication: "Homme, grossesse, comorbidité, > 65 ans",
          traitement: "Fosfomycine 3g dose unique OU Pivmécillinam 400mg x2/j",
          alternatives: "Nitrofurantoïne 100mg x3/j, Amoxicilline 1g x3/j (sur antibiogramme)",
          duree: "7 jours",
          alert: "ECBU obligatoire avant traitement"
        },
        {
          name: "Pyélonéphrite Aiguë Simple",
          indication: "PNA sans signe de gravité",
          traitement: "Ciprofloxacine 500mg x2/j OU Lévofloxacine 500mg x1/j OU Ceftriaxone 1g x1/j",
          alternatives: "Amoxicilline 1g x3/j ou Cotrimoxazole (sur antibiogramme)",
          duree: "7-10 jours selon évolution",
          alert: "Éviter fluoroquinolones si prescription < 6 mois"
        },
        {
          name: "Pyélonéphrite Gravidique",
          indication: "PNA chez femme enceinte",
          traitement: "Ceftriaxone 1g x1/j",
          alternatives: "Aztréonam ou Ciprofloxacine (si allergie)",
          duree: "10 jours",
          alert: "Hospitalisation recommandée - Surveillance rapprochée"
        },
        {
          name: "Infection Urinaire Masculine",
          indication: "Prostatite présumée",
          traitement: "Ciprofloxacine 500mg x2/j OU Lévofloxacine 500mg x1/j OU Ceftriaxone 1g x1/j",
          alternatives: "Cotrimoxazole 800/160mg x2/j (sur antibiogramme)",
          duree: "Asymptomatique: 7j, Symptomatique: 14j, Uropathie: 21j",
          alert: "Durée selon clinique et uropathie"
        }
      ]
    },
    respiratoires: {
      name: "Infections Respiratoires",
      icon: <Wind className="w-5 h-5" />,
      color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
      protocols: [
        {
          name: "Pneumonie Sans Comorbidité",
          indication: "Adulte jeune sain",
          traitement: "Amoxicilline 1g x3/j",
          alternatives: "Pristinamycine 1g x3/j (si allergie pénicilline)",
          duree: "3j si critères stabilité*, 5j si stabilité J3-J5, 7j sinon",
          notes: "*Température ≤37.8°C, PA ≥90mmHg, FC ≤100, FR <24, SpO2 ≥90%",
          alert: "Durée adaptée aux critères de stabilité clinique"
        },
        {
          name: "Pneumonie Avec Comorbidité",
          indication: "BPCO, diabète, insuffisance cardiaque, immunodépression",
          traitement: "Amoxicilline-acide clavulanique 1g x3/j",
          alternatives: "Ceftriaxone 1g/j OU Lévofloxacine 500mg x1/j (allergie sévère)",
          duree: "5-7 jours selon stabilité",
          alert: "Lévofloxacine uniquement sur avis infectiologue"
        },
        {
          name: "Pneumonie Post-grippale",
          indication: "Pneumonie après grippe",
          traitement: "Amoxicilline-acide clavulanique 1g x3/j",
          alternatives: "Ceftriaxone 1g/j OU Pristinamycine 1g x3/j",
          duree: "7 jours",
          notes: "Recherche Staphylococcus aureus"
        },
        {
          name: "Suspicion Bactérie Atypique",
          indication: "Mycoplasme, Chlamydia (hors Legionella)",
          traitement: "Clarithromycine 500mg x2/j",
          alternatives: "Pristinamycine 1g x3/j OU Doxycycline 100mg x2/j",
          duree: "7 jours",
          notes: "Signes cliniques évocateurs: toux sèche, AEG marquée"
        },
        {
          name: "BPCO - Exacerbation Sans FDR",
          indication: "Augmentation volume + purulence expectorations ≥48h",
          traitement: "Amoxicilline 1g x3/j",
          alternatives: "Pristinamycine 1g x3/j (allergie), Clarithromycine 500mg x2/j (rupture stock)",
          duree: "5 jours",
          alert: "Pas d'ATB si < 48h - Réévaluer 48-72h"
        },
        {
          name: "BPCO - Exacerbation Avec FDR",
          indication: "≥2 exacerbations/an, VEMS <50%, insuffisance cardiaque",
          traitement: "Amoxicilline-acide clavulanique 1g x3/j",
          alternatives: "Cotrimoxazole 800/160mg x2/j OU Ceftriaxone 1g/j",
          duree: "5 jours",
          alert: "Avis spécialisé si colonisation Pseudomonas"
        },
        {
          name: "Bronchite Aiguë",
          indication: "Adulte jeune sain",
          traitement: "Pas d'antibiothérapie",
          notes: "Étiologie virale prédominante",
          duree: "N/A"
        },
        {
          name: "Coqueluche - Curatif",
          indication: "Cas confirmé",
          traitement: "Clarithromycine 500mg x2/j",
          alternatives: "Azithromycine 500mg x1/j (3j) OU Érythromycine 1g x2/j (14j)",
          duree: "7 jours (clarithromycine)",
          alert: "Prophylaxie contacts: nourrissons < 6 mois, personnes fragiles"
        }
      ]
    },
    orl: {
      name: "Infections ORL",
      icon: <Stethoscope className="w-5 h-5" />,
      color: "bg-purple-500/10 text-purple-400 border-purple-500/30",
      protocols: [
        {
          name: "Angine à Strepto A",
          indication: "TDR positif chez adulte ou enfant > 3 ans",
          traitement: "Amoxicilline 1g x2/j",
          alternatives: "Cefpodoxime 100mg x2/j (allergie bénigne), Clarithromycine 250mg x2/j (CI β-lactamines)",
          duree: "6 jours (amoxicilline), 5 jours (alternatives)",
          alert: "TDR obligatoire avant ATB"
        },
        {
          name: "Sinusite Maxillaire Adulte",
          indication: "Sinusite purulente > 10 jours",
          traitement: "Amoxicilline 1g x3/j",
          alternatives: "Céfuroxime-axétil 250mg x2/j OU Cefpodoxime 200mg x2/j",
          duree: "7 jours (amoxicilline), 5 jours (C2G)",
          notes: "Étiologie virale prédominante - ATB si échec traitement symptomatique"
        },
        {
          name: "Sinusite d'Origine Dentaire",
          indication: "Sinusite maxillaire + contexte dentaire",
          traitement: "Amoxicilline-acide clavulanique 1g x3/j",
          duree: "7 jours",
          notes: "Nécessite consultation dentaire associée"
        },
        {
          name: "Sinusite Frontale Adulte",
          indication: "Douleur frontale + signes généraux",
          traitement: "Amoxicilline-acide clavulanique 1g x3/j",
          alternatives: "Cefpodoxime 200mg x2/j OU Lévofloxacine 500mg x1/j (CI β-lactamines)",
          duree: "7 jours (augmentin), 5 jours (alternatives)",
          alert: "Risque complications graves - Surveillance"
        },
        {
          name: "OMA Non Compliquée Adulte/Enfant > 2 ans",
          indication: "Otite moyenne aiguë symptomatique",
          traitement: "Amoxicilline 1g x2-3/j",
          alternatives: "Cefpodoxime 200mg x2/j OU Cotrimoxazole 800/160mg x2/j OU Pristinamycine 1g x2/j (adulte)",
          duree: "5 jours",
          notes: "Traiter si symptomatique, récurrente ou perforée"
        },
        {
          name: "OMA Enfant ≤ 2 ans",
          indication: "OMA chez nourrisson/jeune enfant",
          traitement: "Amoxicilline (dose pédiatrique)",
          alternatives: "Cefpodoxime OU Cotrimoxazole (allergie)",
          duree: "10 jours",
          alert: "Si perforée : Amoxicilline-acide clavulanique"
        },
        {
          name: "OMA Récidivante",
          indication: "≥ 3 épisodes en 6 mois",
          traitement: "Amoxicilline-acide clavulanique 1g x2-3/j",
          duree: "5 jours (adulte), 10 jours (enfant)",
          notes: "Rechercher facteurs favorisants"
        }
      ]
    },
    cutanees: {
      name: "Infections Cutanées",
      icon: <Heart className="w-5 h-5" />,
      color: "bg-pink-500/10 text-pink-400 border-pink-500/30",
      protocols: [
        {
          name: "Dermo-Hypodermite Bactérienne Non Nécrosante (DHBNN)",
          indication: "Érysipèle, cellulite",
          traitement: "Amoxicilline (dose selon poids)",
          alternatives: "Clindamycine OU Pristinamycine (allergie)",
          duree: "7 jours",
          notes: "Régression signes cutanés en 2-3 semaines - Ne pas prolonger ATB si évolution favorable",
          alert: "Signes généraux guides - Pas les signes cutanés"
        },
        {
          name: "Fasciite Nécrosante",
          indication: "Infection cutanée nécrosante",
          traitement: "Hospitalisation urgente (Appel 15)",
          notes: "URGENCE VITALE - Chirurgie + ATB large spectre",
          duree: "Avis spécialisé",
          alert: "URGENCE ABSOLUE - Pronostic vital engagé"
        },
        {
          name: "Morsure - DHBNN sur Morsure",
          indication: "Plaie par morsure +/- signes infectieux",
          traitement: "Amoxicilline + acide clavulanique",
          alternatives: "Doxycycline (allergie)",
          duree: "5 jours (prophylaxie), 7 jours (DHBNN), jusqu'à 10j selon évolution",
          alert: "Prophylaxie antirabique selon contexte"
        },
        {
          name: "Abcès",
          indication: "Collection purulente",
          traitement: "Incision-drainage + Clindamycine OU Pristinamycine OU Amoxicilline-acide clav",
          duree: "5 jours",
          notes: "Geste chirurgical primordial. Bénéfice ATB: réduction douleur et récidives",
          alert: "Discuter indication ATB au cas par cas"
        },
        {
          name: "Impétigo Peu Étendu",
          indication: "< 2% surface corporelle OU < 6 lésions",
          traitement: "Nettoyage eau + savon + ATB topique: Mupirocine 2-3/j",
          duree: "5 jours",
          notes: "Éviction 72h si lésions impossibles à couvrir"
        },
        {
          name: "Impétigo Étendu",
          indication: "≥ 2% surface corporelle OU ≥ 6 lésions",
          traitement: "Nettoyage + ATB oral: Pristinamycine (adulte) OU Amoxicilline-acide clav (enfant)",
          duree: "7 jours",
          notes: "Éviction 72h si lésions impossibles à couvrir"
        },
        {
          name: "Furoncle Simple",
          indication: "Furoncle isolé sans complication",
          traitement: "Soins locaux uniquement",
          notes: "Pas d'antibiothérapie systématique"
        },
        {
          name: "Furoncle Compliqué",
          indication: "Multiplication lésions, DHBNN péri-lésionnelle, abcès, fièvre",
          traitement: "Soins locaux + Clindamycine OU Pristinamycine",
          alternatives: "Avis spécialisé (enfant)",
          duree: "7 jours"
        },
        {
          name: "Furonculose",
          indication: "Répétition furoncles sur plusieurs mois",
          traitement: "Poussée: Clindamycine OU Pristinamycine (7j) + Décontamination bactérienne patient et entourage",
          duree: "7 jours + décontamination",
          notes: "Voir protocole décontamination SARM (medqual.fr)"
        }
      ]
    },
    urogenitales: {
      name: "Infections Urogénitales",
      icon: <Zap className="w-5 h-5" />,
      color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
      protocols: [
        {
          name: "Urétrite / Cervicite - Gonocoque",
          indication: "Gonocoque probable ou confirmé",
          traitement: "Ceftriaxone 1g IM ou IV dose unique",
          alternatives: "Gentamicine 240mg IM (CI β-lactamines), Ciprofloxacine 500mg PO",
          duree: "1 jour (dose unique)",
          notes: "Si séjour Asie-Pacifique: Ceftriaxone 1g + Azithromycine 2g"
        },
        {
          name: "Urétrite / Cervicite - Chlamydia trachomatis",
          indication: "Chlamydia probable ou confirmé",
          traitement: "Doxycycline 100mg x2/j",
          alternatives: "Azithromycine 1g dose unique (CI cyclines, grossesse 2-3ème trimestre)",
          duree: "7 jours (doxycycline), 1 jour (azithromycine)",
          notes: "Dépistage et traitement partenaire(s)"
        },
        {
          name: "Urétrite / Cervicite - Mycoplasma genitalium",
          indication: "Mycoplasme génital",
          traitement: "Azithromycine 1g J1, puis 500mg J2-J3",
          alternatives: "Moxifloxacine 400mg/j (résistance macrolides)",
          duree: "3 jours (azithromycine), 7 jours (moxifloxacine)",
          alert: "Avis spécialisé si échec"
        },
        {
          name: "Trichomonas vaginalis",
          indication: "Trichomonase",
          traitement: "Métronidazole 500mg x2/j",
          duree: "7 jours",
          notes: "Traiter simultanément les partenaires"
        }
      ]
    }
  };

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Veuillez décrire la situation clinique");
      return;
    }

    setLoading(true);
    try {
      const context = selectedProtocol 
        ? `Protocole sélectionné: ${selectedProtocol.name}\n\nSituation clinique:\n${aiPrompt}`
        : `Situation clinique infection:\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: "protocole_infection_antibiotherapie"
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success("Analyse IA terminée");
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error("Erreur lors de l'analyse IA");
      setAiResponse("Erreur lors de l'analyse. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = Object.entries(infectionProtocols).reduce((acc, [key, category]) => {
    const filteredProtocols = category.protocols.filter(protocol =>
      protocol.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      protocol.indication.toLowerCase().includes(searchTerm.toLowerCase()) ||
      protocol.traitement.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filteredProtocols.length > 0) {
      acc[key] = { ...category, protocols: filteredProtocols };
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="infections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Shield className="w-8 h-8 text-cyan-400" />
            Protocoles Infections
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Guide d'antibiothérapie avec durées recommandées et analyse IA
          </p>
        </div>
      </div>

      {/* Alert Important */}
      <Alert className="bg-cyan-500/10 border-cyan-500/30">
        <AlertTriangle className="w-5 h-5 text-cyan-400" />
        <AlertDescription className="text-cyan-300">
          <strong>Mémo Antibiothérapie - Septembre 2025</strong><br/>
          Durées basées sur recommandations HAS/SPILF. Valable pour évolution favorable. 
          Pour infections urinaires, CMI non utile pour choix antibiotique.
        </AlertDescription>
      </Alert>

      {/* Search */}
      <Card className="border-[hsl(var(--border))]">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-[hsl(var(--text-dim))]" />
            <Input
              placeholder="Rechercher une infection, un traitement..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Protocols List */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                Protocoles par Type d'Infection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {Object.entries(filteredCategories).map(([key, category]) => (
                  <AccordionItem key={key} value={key} className="border border-[hsl(var(--border))] rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${category.color}`}>
                          {category.icon}
                        </div>
                        <div className="text-left">
                          <div className="font-semibold">{category.name}</div>
                          <div className="text-sm text-[hsl(var(--text-dim))]">
                            {category.protocols.length} protocole(s)
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 mt-3">
                        {category.protocols.map((protocol, idx) => (
                          <div
                            key={idx}
                            className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-[hsl(var(--border))] hover:border-cyan-500/50 transition-colors cursor-pointer"
                            onClick={() => setSelectedProtocol(protocol)}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-white">{protocol.name}</h4>
                              {protocol.alert && (
                                <Badge variant="destructive" className="ml-2">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Alert
                                </Badge>
                              )}
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex gap-2">
                                <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Indication:</span>{' '}
                                  <span className="text-white">{protocol.indication}</span>
                                </div>
                              </div>
                              
                              <div className="flex gap-2">
                                <Syringe className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Traitement:</span>{' '}
                                  <span className="text-white">{protocol.traitement}</span>
                                </div>
                              </div>
                              
                              {protocol.alternatives && (
                                <div className="flex gap-2">
                                  <Pill className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <span className="text-[hsl(var(--text-dim))]">Alternatives:</span>{' '}
                                    <span className="text-white">{protocol.alternatives}</span>
                                  </div>
                                </div>
                              )}
                              
                              <div className="flex gap-2">
                                <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div>
                                  <span className="text-[hsl(var(--text-dim))]">Durée:</span>{' '}
                                  <Badge variant="outline" className="ml-1">{protocol.duree}</Badge>
                                </div>
                              </div>

                              {protocol.notes && (
                                <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300">
                                  <Info className="w-3 h-3 inline mr-1" />
                                  {protocol.notes}
                                </div>
                              )}

                              {protocol.alert && (
                                <Alert className="mt-2 bg-red-500/10 border-red-500/30">
                                  <AlertTriangle className="w-4 h-4 text-red-400" />
                                  <AlertDescription className="text-red-300 text-xs">
                                    {protocol.alert}
                                  </AlertDescription>
                                </Alert>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* AI Analysis Panel */}
        <div className="space-y-4">
          {selectedProtocol && (
            <Card className="border-cyan-500/30 bg-cyan-500/5">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Protocole Sélectionné
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <h3 className="font-bold text-white">{selectedProtocol.name}</h3>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="text-[hsl(var(--text-dim))]">Traitement:</span>
                      <p className="text-white">{selectedProtocol.traitement}</p>
                    </div>
                    <div>
                      <span className="text-[hsl(var(--text-dim))]">Durée:</span>
                      <p className="text-white">{selectedProtocol.duree}</p>
                    </div>
                    {selectedProtocol.exception && (
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Exception:</span>
                        <p className="text-yellow-300 text-xs">{selectedProtocol.exception}</p>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => setSelectedProtocol(null)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    Désélectionner
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez la situation clinique (âge, terrain, signes, allergies...)&#10;&#10;Exemple: Femme 35 ans, cystite aiguë, brûlures mictionnelles depuis 24h, pas d'allergie connue"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={6}
                className="resize-none"
              />
              
              <Button
                onClick={handleAiAnalysis}
                disabled={loading || !aiPrompt.trim()}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision. Elle ne remplace pas le jugement clinique. 
                      Toujours adapter selon antibiogramme et évolution clinique.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
